import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { masterService } from '../../services/masterService';
import { nablConfigService } from '../../services/nablConfigService';
import { FILE_BASE_URL } from '../../services/api';
import './ManageNablPage.css';

export const ACCREDITATION_OPTIONS = [
  { value: 0, label: 'NA', fullLabel: 'Not Accredited', class: 'pill-na', desc: 'Standard Report (No badge/logo)' },
  { value: 1, label: 'NABL', fullLabel: 'NABL Accredited', class: 'pill-nabl', desc: 'Renders NABL logo & watermark' },
  { value: 2, label: 'CAP', fullLabel: 'CAP Accredited', class: 'pill-cap', desc: 'Renders College of American Pathologists logo' },
  { value: 3, label: 'NABH', fullLabel: 'NABH Accredited', class: 'pill-nabh', desc: 'Renders NABH Hospital/Lab accreditation logo' },
  { value: 5, label: 'NABL+CAP', fullLabel: 'NABL + CAP', class: 'pill-nabl-cap', desc: 'Dual accreditation: NABL and CAP logos rendered' },
  { value: 4, label: 'NABH+NABL', fullLabel: 'NABH + NABL', class: 'pill-nabh-nabl', desc: 'Dual accreditation: NABH and NABL logos rendered' },
  { value: 7, label: 'NABH+CAP', fullLabel: 'NABH + CAP', class: 'pill-nabh-cap', desc: 'Dual accreditation: NABH and CAP logos rendered' },
  { value: 6, label: 'All 3', fullLabel: 'NABH + NABL + CAP', class: 'pill-triple', desc: 'Triple accreditation: All 3 logos displayed' }
];

export const ManageNablPage = () => {
  // Master Filter States
  const [centres, setCentres] = useState([]);
  const [selectedCentreId, setSelectedCentreId] = useState('');
  const [departments, setDepartments] = useState([]);
  const [selectedDeptId, setSelectedDeptId] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchMode, setSearchMode] = useState('contains'); // 'contains' | 'starts_with'
  const [filterChip, setFilterChip] = useState('all'); // 'all' | 'configured' | 'unconfigured' | '1' | '2' | '3' | 'multi'

  // Data & Modification State
  const [investigations, setInvestigations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Logo Management State
  const [logoModalOpen, setLogoModalOpen] = useState(false);
  const [centreLogos, setCentreLogos] = useState(null);
  const [selectedLogoType, setSelectedLogoType] = useState(1); // 1=NABL, 2=CAP, 3=NABH
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const showToast = (text, type = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 4000);
  };

  // 1. Initial Load: Fetch Centres and Departments
  useEffect(() => {
    const loadMasters = async () => {
      try {
        const [centresRes, deptsRes] = await Promise.all([
          masterService.getCentres({ isActive: true }),
          masterService.getDepartments({ isActive: true })
        ]);

        const centresList = centresRes.data || [];
        setCentres(centresList);
        if (centresList.length > 0) {
          setSelectedCentreId(centresList[0].centreId.toString());
        }

        setDepartments(deptsRes.data || []);
      } catch (err) {
        showToast('Failed to load centres or departments catalog.', 'error');
      }
    };

    loadMasters();
  }, []);

  // 2. Fetch Investigations whenever Centre changes
  const fetchInvestigations = useCallback(async () => {
    if (!selectedCentreId) return;

    setLoading(true);
    try {
      const res = await nablConfigService.getNablInvestigations({
        centreId: parseInt(selectedCentreId, 10),
        subCategoryId: selectedDeptId === 'All' ? null : selectedDeptId
      });

      const rawItems = res.data || [];
      const mapped = rawItems.map(item => ({
        ...item,
        originalIsNabl: item.isNabl,
        isModified: false,
        isSelected: false
      }));

      setInvestigations(mapped);
    } catch (err) {
      showToast('Error loading report accreditation configurations.', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedCentreId, selectedDeptId]);

  useEffect(() => {
    fetchInvestigations();
  }, [fetchInvestigations]);

  // 3. Fetch Centre Logos when Logo modal opens
  const fetchLogos = useCallback(async () => {
    if (!selectedCentreId) return;
    try {
      const res = await nablConfigService.getLogos(selectedCentreId);
      setCentreLogos(res.data || null);
    } catch (err) {
      showToast('Error loading centre logos.', 'error');
    }
  }, [selectedCentreId]);

  useEffect(() => {
    if (logoModalOpen) {
      fetchLogos();
      setLogoFile(null);
      setLogoPreview(null);
    }
  }, [logoModalOpen, fetchLogos]);

  // 4. Update single investigation accreditation
  const handleAccreditationChange = (invId, newValue) => {
    setInvestigations(prev =>
      prev.map(item => {
        if (item.investigationId === invId) {
          const isModified = newValue !== item.originalIsNabl;
          return { ...item, isNabl: newValue, isModified };
        }
        return item;
      })
    );
  };

  // 5. Checkbox selection
  const handleToggleSelect = (invId) => {
    setInvestigations(prev =>
      prev.map(item =>
        item.investigationId === invId
          ? { ...item, isSelected: !item.isSelected }
          : item
      )
    );
  };

  const handleSelectAllVisible = (checked) => {
    const visibleIds = new Set(filteredInvestigations.map(i => i.investigationId));
    setInvestigations(prev =>
      prev.map(item =>
        visibleIds.has(item.investigationId)
          ? { ...item, isSelected: checked }
          : item
      )
    );
  };

  // 6. Batch Apply on Selected Items
  const handleBatchApply = (targetValue) => {
    const selectedCount = investigations.filter(i => i.isSelected).length;
    if (selectedCount === 0) {
      showToast('Please select one or more investigations to apply batch changes.', 'error');
      return;
    }

    setInvestigations(prev =>
      prev.map(item => {
        if (item.isSelected) {
          const isModified = targetValue !== item.originalIsNabl;
          return { ...item, isNabl: targetValue, isModified };
        }
        return item;
      })
    );

    const opt = ACCREDITATION_OPTIONS.find(o => o.value === targetValue);
    showToast(`Applied ${opt?.label || 'accreditation'} to ${selectedCount} selected investigation(s).`);
  };

  // 7. Discard all unsaved changes
  const handleDiscardChanges = () => {
    setInvestigations(prev =>
      prev.map(item => ({
        ...item,
        isNabl: item.originalIsNabl,
        isModified: false
      }))
    );
    showToast('All unsaved modifications were reverted.', 'info');
  };

  // 8. Batch Save to Backend
  const handleSaveAll = async () => {
    const modifiedItems = investigations.filter(i => i.isModified);
    if (modifiedItems.length === 0) {
      showToast('No changes detected to save.', 'info');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        centreId: parseInt(selectedCentreId, 10),
        items: modifiedItems.map(i => ({
          subCategoryId: i.subCategoryId,
          investigationId: i.investigationId,
          isNabl: i.isNabl
        }))
      };

      const res = await nablConfigService.saveBatch(payload);
      showToast(`Successfully saved ${res.data?.savedCount || modifiedItems.length} investigation configuration(s)!`);

      // Update baseline originalIsNabl
      setInvestigations(prev =>
        prev.map(item => ({
          ...item,
          originalIsNabl: item.isNabl,
          isModified: false
        }))
      );
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save configuration updates.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // 9. Filter logic
  const filteredInvestigations = useMemo(() => {
    let result = investigations;

    // Subcategory Filter
    if (selectedDeptId !== 'All') {
      result = result.filter(i => i.subCategoryId === selectedDeptId);
    }

    // Text Search
    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase().trim();
      result = result.filter(i => {
        const name = (i.invName || '').toLowerCase();
        const code = (i.invCode || '').toLowerCase();
        if (searchMode === 'starts_with') {
          return name.startsWith(query) || code.startsWith(query);
        }
        return name.includes(query) || code.includes(query);
      });
    }

    // Accreditation Chip Filter
    if (filterChip === 'configured') {
      result = result.filter(i => i.isNabl > 0);
    } else if (filterChip === 'unconfigured') {
      result = result.filter(i => i.isNabl === 0);
    } else if (filterChip === '1') {
      result = result.filter(i => i.isNabl === 1);
    } else if (filterChip === '2') {
      result = result.filter(i => i.isNabl === 2);
    } else if (filterChip === '3') {
      result = result.filter(i => i.isNabl === 3);
    } else if (filterChip === 'multi') {
      result = result.filter(i => [4, 5, 6, 7].includes(i.isNabl));
    }

    return result;
  }, [investigations, selectedDeptId, searchTerm, searchMode, filterChip]);

  // Summary Metrics
  const metrics = useMemo(() => {
    const total = investigations.length;
    const configured = investigations.filter(i => i.isNabl > 0).length;
    const nablOnly = investigations.filter(i => i.isNabl === 1).length;
    const capOnly = investigations.filter(i => i.isNabl === 2).length;
    const multiAccredited = investigations.filter(i => [4, 5, 6, 7].includes(i.isNabl)).length;
    const unconfigured = investigations.filter(i => i.isNabl === 0).length;
    const modifiedCount = investigations.filter(i => i.isModified).length;
    const selectedCount = investigations.filter(i => i.isSelected).length;

    return {
      total,
      configured,
      nablOnly,
      capOnly,
      multiAccredited,
      unconfigured,
      modifiedCount,
      selectedCount
    };
  }, [investigations]);

  // Logo file selection & upload
  const handleLogoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.match(/^image\/(png|jpeg|jpg|webp)$/)) {
      showToast('Allowed logo formats: PNG, JPG, or WEBP only.', 'error');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showToast('Logo file size must be less than 5MB.', 'error');
      return;
    }

    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadLogoSubmit = async () => {
    if (!logoPreview || !logoFile) {
      showToast('Please select a logo image file to upload.', 'error');
      return;
    }

    setLogoUploading(true);
    try {
      await nablConfigService.uploadLogo({
        centreId: parseInt(selectedCentreId, 10),
        logoType: selectedLogoType,
        base64Image: logoPreview,
        fileName: logoFile.name
      });

      showToast('Accreditation logo uploaded and applied successfully!');
      fetchLogos();
      setLogoFile(null);
      setLogoPreview(null);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to upload logo image.', 'error');
    } finally {
      setLogoUploading(false);
    }
  };

  const allVisibleSelected = filteredInvestigations.length > 0 &&
    filteredInvestigations.every(i => i.isSelected);

  const currentCentreName = centres.find(c => c.centreId.toString() === selectedCentreId)?.centreName || 'Diagnostic Centre';

  return (
    <div className="nabl-page-container">
      {/* Toast Alert */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: '1.5rem',
            right: '2rem',
            zIndex: 9999,
            padding: '0.75rem 1.25rem',
            borderRadius: '8px',
            backgroundColor: toast.type === 'error' ? '#ef4444' : toast.type === 'info' ? '#3b82f6' : '#10b981',
            color: 'white',
            fontWeight: 600,
            fontSize: '0.875rem',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          {toast.text}
        </div>
      )}

      {/* Header */}
      <header className="nabl-header">
        <div className="nabl-header-left">
          <div className="nabl-title-row">
            <div className="nabl-badge-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="8" r="7" />
                <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
              </svg>
            </div>
            <div>
              <h1 className="nabl-title">Investigation Report Configuration</h1>
              <p className="nabl-subtitle">
                Manage NABL, CAP & NABH accreditations, report watermarks, and header logos for <strong>{currentCentreName}</strong>
              </p>
            </div>
          </div>
        </div>

        <div className="nabl-header-actions">
          <button
            type="button"
            className="btn-modern btn-modern-secondary"
            onClick={() => setLogoModalOpen(true)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
            Centre Logos
          </button>

          <button
            type="button"
            className="btn-modern btn-modern-outline"
            onClick={fetchInvestigations}
            disabled={loading}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
            </svg>
            Refresh
          </button>
        </div>
      </header>

      {/* Filters & Search Panel */}
      <section className="nabl-filter-card">
        <div className="nabl-filter-grid">
          {/* Centre Selector */}
          <div className="nabl-filter-group">
            <label className="nabl-filter-label" htmlFor="nablCentre">
              Diagnostic Centre
            </label>
            <select
              id="nablCentre"
              className="nabl-select"
              value={selectedCentreId}
              onChange={(e) => setSelectedCentreId(e.target.value)}
            >
              {centres.map(c => (
                <option key={c.centreId} value={c.centreId}>
                  {c.centreName} {c.centreCode ? `(${c.centreCode})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Department / Subcategory Filter */}
          <div className="nabl-filter-group">
            <label className="nabl-filter-label" htmlFor="nablDept">
              Department / Subcategory
            </label>
            <select
              id="nablDept"
              className="nabl-select"
              value={selectedDeptId}
              onChange={(e) => setSelectedDeptId(e.target.value)}
            >
              <option value="All">All Departments ({departments.length})</option>
              {departments.map(d => (
                <option key={d.departmentId} value={d.departmentId}>
                  {d.departmentName}
                </option>
              ))}
            </select>
          </div>

          {/* Test Name / Code Search */}
          <div className="nabl-filter-group" style={{ gridColumn: 'span 2' }}>
            <div className="nabl-filter-label">
              <span>Search Investigation</span>
              <span style={{ textTransform: 'none', fontWeight: 500, color: '#64748b' }}>
                Mode:
                <button
                  type="button"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#0284c7',
                    fontWeight: 600,
                    cursor: 'pointer',
                    marginLeft: '4px'
                  }}
                  onClick={() => setSearchMode(prev => prev === 'contains' ? 'starts_with' : 'contains')}
                >
                  {searchMode === 'contains' ? 'Contains' : 'Starts With'}
                </button>
              </span>
            </div>
            <div className="search-input-wrapper">
              <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                className="nabl-input"
                placeholder="Type investigation name or code (e.g., CBC, Lipid, HbA1c)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="search-clear-btn"
                  onClick={() => setSearchTerm('')}
                  title="Clear search"
                >
                  &times;
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Filter Chips Bar */}
        <div className="nabl-chips-bar">
          <span className="filter-chip-label">Quick Filter:</span>
          <button
            type="button"
            className={`filter-chip ${filterChip === 'all' ? 'active' : ''}`}
            onClick={() => setFilterChip('all')}
          >
            All Tests <span className="chip-count">{metrics.total}</span>
          </button>
          <button
            type="button"
            className={`filter-chip ${filterChip === 'configured' ? 'active' : ''}`}
            onClick={() => setFilterChip('configured')}
          >
            Accredited <span className="chip-count">{metrics.configured}</span>
          </button>
          <button
            type="button"
            className={`filter-chip ${filterChip === 'unconfigured' ? 'active' : ''}`}
            onClick={() => setFilterChip('unconfigured')}
          >
            Not Accredited (NA) <span className="chip-count">{metrics.unconfigured}</span>
          </button>
          <button
            type="button"
            className={`filter-chip ${filterChip === '1' ? 'active' : ''}`}
            onClick={() => setFilterChip('1')}
          >
            NABL Only <span className="chip-count">{metrics.nablOnly}</span>
          </button>
          <button
            type="button"
            className={`filter-chip ${filterChip === '2' ? 'active' : ''}`}
            onClick={() => setFilterChip('2')}
          >
            CAP Only <span className="chip-count">{metrics.capOnly}</span>
          </button>
          <button
            type="button"
            className={`filter-chip ${filterChip === 'multi' ? 'active' : ''}`}
            onClick={() => setFilterChip('multi')}
          >
            Multi-Accredited <span className="chip-count">{metrics.multiAccredited}</span>
          </button>
        </div>
      </section>

      {/* Metrics Row */}
      <section className="nabl-metrics-row">
        <div className="nabl-metric-card highlight">
          <span className="nabl-metric-title">Total Catalog Tests</span>
          <span className="nabl-metric-val">{metrics.total}</span>
        </div>
        <div className="nabl-metric-card success">
          <span className="nabl-metric-title">Accredited Tests</span>
          <span className="nabl-metric-val">{metrics.configured}</span>
        </div>
        <div className="nabl-metric-card">
          <span className="nabl-metric-title">NABL Active</span>
          <span className="nabl-metric-val">{metrics.nablOnly}</span>
        </div>
        <div className="nabl-metric-card">
          <span className="nabl-metric-title">Multi-Accredited</span>
          <span className="nabl-metric-val">{metrics.multiAccredited}</span>
        </div>
        <div className="nabl-metric-card warning">
          <span className="nabl-metric-title">Pending Changes</span>
          <span className="nabl-metric-val" style={{ color: metrics.modifiedCount > 0 ? '#b45309' : '#64748b' }}>
            {metrics.modifiedCount}
          </span>
        </div>
      </section>

      {/* Batch Operation Bar (When items are selected) */}
      {metrics.selectedCount > 0 && (
        <section className="nabl-batch-toolbar">
          <div className="batch-info">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 11 12 14 22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
            <span>{metrics.selectedCount} test(s) selected</span>
          </div>

          <div className="batch-actions-group">
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569' }}>Apply to Selected:</span>
            <button
              type="button"
              className="batch-apply-btn"
              onClick={() => handleBatchApply(1)}
              style={{ color: '#0284c7', borderColor: '#bae6fd' }}
            >
              NABL
            </button>
            <button
              type="button"
              className="batch-apply-btn"
              onClick={() => handleBatchApply(2)}
              style={{ color: '#059669', borderColor: '#a7f3d0' }}
            >
              CAP
            </button>
            <button
              type="button"
              className="batch-apply-btn"
              onClick={() => handleBatchApply(5)}
              style={{ color: '#0d9488', borderColor: '#99f6e4' }}
            >
              NABL + CAP
            </button>
            <button
              type="button"
              className="batch-apply-btn"
              onClick={() => handleBatchApply(3)}
              style={{ color: '#7c3aed', borderColor: '#ddd6fe' }}
            >
              NABH
            </button>
            <button
              type="button"
              className="batch-apply-btn"
              onClick={() => handleBatchApply(6)}
              style={{ color: '#ea580c', borderColor: '#fed7aa' }}
            >
              All Three
            </button>
            <button
              type="button"
              className="batch-apply-btn"
              onClick={() => handleBatchApply(0)}
              style={{ color: '#64748b' }}
            >
              Set NA
            </button>
          </div>
        </section>
      )}

      {/* Main Configuration Grid */}
      <section className="nabl-table-container">
        <div className="nabl-table-header-row">
          <div className="nabl-table-count">
            Showing {filteredInvestigations.length} of {investigations.length} investigations
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
              type="button"
              className="btn-modern btn-modern-secondary"
              style={{ padding: '0.35rem 0.75rem', fontSize: '0.78rem' }}
              onClick={() => handleSelectAllVisible(!allVisibleSelected)}
            >
              {allVisibleSelected ? 'Deselect Visible' : 'Select All Visible'}
            </button>
          </div>
        </div>

        <div className="table-responsive">
          {loading ? (
            <div className="nabl-empty-state">
              <div className="nabl-empty-icon">⏳</div>
              <p>Loading investigation configurations for {currentCentreName}...</p>
            </div>
          ) : filteredInvestigations.length === 0 ? (
            <div className="nabl-empty-state">
              <div className="nabl-empty-icon">🔍</div>
              <p>No investigations match the selected filters or search criteria.</p>
              <button
                type="button"
                className="btn-modern btn-modern-outline"
                style={{ marginTop: '0.5rem' }}
                onClick={() => {
                  setSelectedDeptId('All');
                  setSearchTerm('');
                  setFilterChip('all');
                }}
              >
                Reset All Filters
              </button>
            </div>
          ) : (
            <table className="nabl-grid">
              <thead>
                <tr>
                  <th style={{ width: '40px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={(e) => handleSelectAllVisible(e.target.checked)}
                      title="Select / Deselect all visible"
                    />
                  </th>
                  <th style={{ width: '50px' }}>#</th>
                  <th style={{ width: '160px' }}>Department</th>
                  <th style={{ minWidth: '220px' }}>Investigation / Test</th>
                  <th style={{ minWidth: '450px' }}>Report Accreditation Badge</th>
                  <th style={{ width: '100px', textAlign: 'center' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvestigations.map((item, idx) => {
                  return (
                    <tr
                      key={`${item.subCategoryId}_${item.investigationId}`}
                      className={`${item.isModified ? 'row-modified' : ''} ${item.isSelected ? 'row-selected' : ''}`}
                    >
                      {/* Checkbox */}
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={item.isSelected}
                          onChange={() => handleToggleSelect(item.investigationId)}
                        />
                      </td>

                      {/* Serial Number */}
                      <td style={{ color: '#64748b', fontWeight: 600 }}>{idx + 1}</td>

                      {/* Department */}
                      <td>
                        <span style={{ fontWeight: 600, color: '#334155' }}>
                          {item.deptName || 'General'}
                        </span>
                      </td>

                      {/* Test Name & Code */}
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontWeight: 600, color: '#0f172a' }}>
                            {item.invName}
                          </span>
                          {item.invCode && (
                            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                              Code: {item.invCode}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Segmented Accreditation Selector */}
                      <td>
                        <div className="segmented-accreditation">
                          {ACCREDITATION_OPTIONS.map(opt => {
                            const isSelected = item.isNabl === opt.value;
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                className={`segmented-pill ${opt.class} ${isSelected ? 'active' : ''}`}
                                onClick={() => handleAccreditationChange(item.investigationId, opt.value)}
                                title={`${opt.fullLabel}: ${opt.desc}`}
                              >
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                      </td>

                      {/* Status / Modification indicator */}
                      <td style={{ textAlign: 'center' }}>
                        {item.isModified ? (
                          <span className="badge-modified" title="Unsaved change ready to persist">
                            ● Modified
                          </span>
                        ) : item.isNabl > 0 ? (
                          <span
                            className="badge-status"
                            style={{
                              background: '#ecfdf5',
                              color: '#047857',
                              border: '1px solid #a7f3d0'
                            }}
                          >
                            Configured
                          </span>
                        ) : (
                          <span
                            className="badge-status"
                            style={{
                              background: '#f1f5f9',
                              color: '#64748b',
                              border: '1px solid #e2e8f0'
                            }}
                          >
                            Default
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Sticky Action Bar (When modifications exist) */}
      {metrics.modifiedCount > 0 && (
        <aside className="nabl-sticky-bar">
          <div className="sticky-bar-left">
            <span className="unsaved-dot" />
            <span>
              <strong>{metrics.modifiedCount}</strong> investigation configuration(s) modified for{' '}
              <strong>{currentCentreName}</strong>.
            </span>
          </div>

          <div className="sticky-bar-right">
            <button
              type="button"
              className="btn-modern btn-modern-secondary"
              onClick={handleDiscardChanges}
              disabled={saving}
            >
              Discard Changes
            </button>
            <button
              type="button"
              className="btn-modern btn-modern-primary"
              onClick={handleSaveAll}
              disabled={saving}
            >
              {saving ? 'Saving Changes...' : 'Save Configuration'}
            </button>
          </div>
        </aside>
      )}

      {/* Centre Logo Management Modal */}
      {logoModalOpen && (
        <div className="modal-overlay" onClick={() => setLogoModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Centre Accreditation Logos — {currentCentreName}</h2>
              <button
                type="button"
                style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#64748b' }}
                onClick={() => setLogoModalOpen(false)}
              >
                &times;
              </button>
            </div>

            <div className="modal-body">
              {/* Logo Tabs: NABL, CAP, NABH */}
              <div className="logo-tabs">
                <button
                  type="button"
                  className={`logo-tab-btn ${selectedLogoType === 1 ? 'active' : ''}`}
                  onClick={() => { setSelectedLogoType(1); setLogoFile(null); setLogoPreview(null); }}
                >
                  NABL Logo {centreLogos?.isNabl && '✓'}
                </button>
                <button
                  type="button"
                  className={`logo-tab-btn ${selectedLogoType === 2 ? 'active' : ''}`}
                  onClick={() => { setSelectedLogoType(2); setLogoFile(null); setLogoPreview(null); }}
                >
                  CAP Logo {centreLogos?.isCap && '✓'}
                </button>
                <button
                  type="button"
                  className={`logo-tab-btn ${selectedLogoType === 3 ? 'active' : ''}`}
                  onClick={() => { setSelectedLogoType(3); setLogoFile(null); setLogoPreview(null); }}
                >
                  NABH Logo {centreLogos?.isNabh && '✓'}
                </button>
              </div>

              {/* Logo Preview and Status */}
              <div className="logo-preview-box">
                {logoPreview ? (
                  <>
                    <img src={logoPreview} alt="Upload preview" className="logo-img-preview" />
                    <span style={{ fontSize: '0.8rem', color: '#059669', fontWeight: 600 }}>
                      New file selected: {logoFile?.name} ({(logoFile?.size / 1024).toFixed(1)} KB)
                    </span>
                  </>
                ) : selectedLogoType === 1 && centreLogos?.nablLogoPath ? (
                  <>
                    <img
                      src={`${FILE_BASE_URL}${centreLogos.nablLogoPath}`}
                      alt="Current NABL Logo"
                      className="logo-img-preview"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                      Current NABL logo active in patient report headers
                    </span>
                  </>
                ) : selectedLogoType === 2 && centreLogos?.capLogoPath ? (
                  <>
                    <img
                      src={`${FILE_BASE_URL}${centreLogos.capLogoPath}`}
                      alt="Current CAP Logo"
                      className="logo-img-preview"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                      Current CAP logo active in patient report headers
                    </span>
                  </>
                ) : selectedLogoType === 3 && centreLogos?.nabhLogoPath ? (
                  <>
                    <img
                      src={`${FILE_BASE_URL}${centreLogos.nabhLogoPath}`}
                      alt="Current NABH Logo"
                      className="logo-img-preview"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                      Current NABH logo active in patient report headers
                    </span>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', color: '#94a3b8' }}>
                    <p style={{ margin: 0, fontWeight: 500 }}>No logo configured for this accreditation badge yet.</p>
                    <span style={{ fontSize: '0.75rem' }}>Upload a PNG or JPG logo (transparent background recommended)</span>
                  </div>
                )}

                <div style={{ marginTop: '0.5rem' }}>
                  <input
                    type="file"
                    id="logoFileInput"
                    accept="image/png, image/jpeg, image/webp"
                    style={{ display: 'none' }}
                    onChange={handleLogoFileChange}
                  />
                  <label htmlFor="logoFileInput" className="btn-modern btn-modern-outline" style={{ cursor: 'pointer' }}>
                    {logoPreview || (selectedLogoType === 1 && centreLogos?.nablLogoPath) || (selectedLogoType === 2 && centreLogos?.capLogoPath) || (selectedLogoType === 3 && centreLogos?.nabhLogoPath) ? 'Choose Replacement File' : 'Browse Logo Image'}
                  </label>
                </div>
              </div>

              <div style={{ fontSize: '0.78rem', color: '#64748b', background: '#f8fafc', padding: '0.75rem', borderRadius: '6px' }}>
                💡 <strong>Report Output Notice:</strong> Uploaded logos will automatically print on all pathology and radiology patient reports for investigations marked with this accreditation badge under <strong>{currentCentreName}</strong>.
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-modern btn-modern-secondary"
                onClick={() => setLogoModalOpen(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="btn-modern btn-modern-primary"
                onClick={handleUploadLogoSubmit}
                disabled={!logoFile || logoUploading}
              >
                {logoUploading ? 'Uploading...' : 'Save & Update Logo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageNablPage;
